export type ConversationMessageSender = "human" | "ai" | "system" | "tool";

function isMarkdownTableSeparator(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.includes("|")) {
    return false;
  }

  const normalized = trimmed.replace(/^\|/, "").replace(/\|$/, "");
  const columns = normalized.split("|").map((column) => column.trim());
  if (columns.length < 2) {
    return false;
  }

  return columns.every((column) => /^:?-{3,}:?$/.test(column));
}

export function hasMarkdownTableSyntax(content: string): boolean {
  const lines = content.split("\n");

  for (let index = 0; index < lines.length - 1; index += 1) {
    const header = lines[index]?.trim() ?? "";
    const separator = lines[index + 1]?.trim() ?? "";
    if (!header || !separator) {
      continue;
    }

    if (!header.includes("|")) {
      continue;
    }

    if (isMarkdownTableSeparator(separator)) {
      return true;
    }
  }

  return false;
}

export function shouldUseGfm(
  sender: ConversationMessageSender,
  content: string,
): boolean {
  return (
    (sender === "ai" || sender === "tool") && hasMarkdownTableSyntax(content)
  );
}

export function normalizeLiteralEscapes(content: string): string {
  if (!content || !/\\[nrt]/.test(content)) {
    return content;
  }

  // Match fenced blocks (3+ backticks or tildes) and inline code spans
  // (1+ backticks). In each case the closing delimiter must be the same
  // length as the opening one (via backreferences), so embedded shorter
  // runs of the same delimiter (e.g. ```...``` inside a ````...```` block,
  // or a lone ` inside ``...``) don't close the segment early.
  const codeOrFenceSegmentPattern =
    /(`{3,})[\s\S]*?\1|(~{3,})[\s\S]*?\2|(`+)(?:[^`\n]|`(?!\3))*\3/g;

  const transformProse = (segment: string): string =>
    segment
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "\n")
      .replace(/\\t/g, "\t");

  let result = "";
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = codeOrFenceSegmentPattern.exec(content)) !== null) {
    if (match.index > lastIndex) {
      result += transformProse(content.slice(lastIndex, match.index));
    }
    result += match[0];
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    result += transformProse(content.slice(lastIndex));
  }

  return result;
}

export function sanitizeMarkdownUrl(url: string): string {
  if (!url) {
    return "";
  }
  if (url.startsWith("#") || url.startsWith("/")) {
    return url;
  }

  try {
    const parsedUrl = new URL(url, "https://sanitized.local");
    const isRelativePath =
      parsedUrl.origin === "https://sanitized.local" && !url.includes(":");
    if (isRelativePath) {
      return url;
    }

    if (
      parsedUrl.protocol === "http:" ||
      parsedUrl.protocol === "https:" ||
      parsedUrl.protocol === "mailto:" ||
      parsedUrl.protocol === "tel:"
    ) {
      return url;
    }
  } catch (_error) {
    return "";
  }

  return "";
}
